import React, { useEffect, useState } from 'react';
import { tryParseJson, formatMetricLabel, formatMetricValue } from './utils';
import './styles.css';

/**
 * ShadowWidget Component
 * 
 * REFACTORED: Handles both flat and hierarchical shadow data
 * Data structure: system -> subsystem -> attributes (hierarchical)
 * OR flat key-value pairs
 * 
 * @param {Object} props
 * @param {Object} props.data - Shadow analysis data
 * @param {Function} props.onComplete - Callback when rendering is complete
 */
const ShadowWidget = ({ data, onComplete }) => {
    const [shadowData, setShadowData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (data && data. response) {
            processShadowData(data.response);
        }
    }, [data]);

    const processShadowData = (responseData) => {
        try {
            const parsedData = tryParseJson(responseData);
            
            if (parsedData && typeof parsedData === 'object') {
                setShadowData(parsedData);
            }
            
            setIsLoading(false);
            
            if (onComplete) {
                onComplete('Shadow widget rendered');
            }
        } catch (err) {
            console.error('[ShadowWidget] Error processing data:', err);
            setIsLoading(false);
        }
    };

    const renderFlatData = (dataObj) => {
        const metrics = Object.keys(dataObj);
        
        return (
            <div className="kpi-metrics">
                {metrics.map((metricKey) => {
                    const metricValue = dataObj[metricKey];
                    const formattedLabel = formatMetricLabel(metricKey);
                    const formattedValue = formatMetricValue(metricValue);
                    
                    return (
                        <div key={metricKey} className="kpi-metric shadow">
                            <span className="kpi-metric-icon">🌓</span>
                            <div className="kpi-metric-content">
                                <span className="kpi-metric-label">{formattedLabel}</span>
                                <span className="kpi-metric-value">{formattedValue}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderHierarchicalData = (dataObj) => {
        const systems = Object.keys(dataObj);
        
        return (
            <div className="shadow-hierarchical-container">
                {systems. map((systemName) => {
                    const systemData = dataObj[systemName];
                    
                    if (!systemData || typeof systemData !== 'object') {
                        return null;
                    }
                    
                    const subsystems = Object.keys(systemData);
                    
                    return (
                        <div key={systemName} className="shadow-system-card">
                            <h3 className="shadow-system-title">
                                <span className="kpi-system-icon">🏗️</span>
                                {formatMetricLabel(systemName)}
                            </h3>
                            
                            <div className="shadow-subsystems-grid">
                                {subsystems.map((subsystemName) => {
                                    const subsystemData = systemData[subsystemName];
                                    
                                    if (!subsystemData || typeof subsystemData !== 'object') {
                                        return null;
                                    }
                                    
                                    const attributes = Object.keys(subsystemData);
                                    
                                    return (
                                        <div key={subsystemName} className="shadow-subsystem-card">
                                            <h4 className="shadow-subsystem-title">
                                                {formatMetricLabel(subsystemName)}
                                            </h4>
                                            
                                            <div className="kpi-metrics">
                                                {attributes.map((attrKey) => {
                                                    const attrValue = subsystemData[attrKey];
                                                    
                                                    return (
                                                        <div key={attrKey} className="kpi-metric shadow">
                                                            <span className="kpi-metric-icon">🌓</span>
                                                            <div className="kpi-metric-content">
                                                                <span className="kpi-metric-label">
                                                                    {formatMetricLabel(attrKey)}
                                                                </span>
                                                                <span className="kpi-metric-value">
                                                                    {formatMetricValue(attrValue)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (isLoading) {
        return <div className="archie-widget-loading">Loading shadow data... </div>;
    }

    if (!shadowData || typeof shadowData !== 'object') {
        return (
            <div className="kpi-empty-state">
                <p>No shadow data available</p>
            </div>
        );
    }

    const title = data.sheetTitle || 'Shadow Analysis';
    const description = data.sheetDescription || 'Shadow coverage and timing analysis';

    // Detect if data is hierarchical or flat
    const firstKey = Object.keys(shadowData)[0];
    const firstValue = shadowData[firstKey];
    const isHierarchical = firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue);

    return (
        <div className="csv-data-outer shadow-widget-container">
            <h2>{title}</h2>
            <h4>{description}</h4>
            
            {isHierarchical ?  renderHierarchicalData(shadowData) : renderFlatData(shadowData)}
        </div>
    );
};

export default ShadowWidget;